/**
 * impersonationSession — helpers for the admin impersonation session lifecycle.
 *
 * WHY THIS EXISTS (critical):
 * The impersonation on app.deskbay.io reuses the SAME server-side Supabase
 * session as the admin panel (the panel's access+refresh tokens are handed
 * over via the URL hash). Calling `supabase.auth.signOut()` here — even with
 * scope 'local' — revokes that session SERVER-SIDE, so the admin panel gets
 * silently logged out at its next token refresh (up to ~1 hour later).
 *
 * Exiting an impersonation must therefore remove ONLY the local copy of the
 * session on this origin, leaving the shared server-side session untouched
 * for the admin panel that is still using it.
 */

import { supabase } from '@deskbay/shared/services/supabase/client';

/**
 * Remove the auth session from THIS origin's storage without any server call.
 * Falls back to scanning localStorage for the standard `sb-*-auth-token` key
 * if the client internals are not accessible (future supabase-js versions).
 */
export async function removeLocalAuthSessionOnly(): Promise<void> {
    try {
        const auth = supabase.auth as unknown as {
            storageKey?: string;
            storage?: { removeItem(key: string): void | Promise<void> };
            stopAutoRefresh?: () => Promise<void>;
        };

        // Stop the auto-refresh timer so no token rotation fires between now
        // and the redirect (a rotation would invalidate the panel's copy).
        if (auth.stopAutoRefresh) await auth.stopAutoRefresh();

        if (auth.storageKey && auth.storage) {
            await auth.storage.removeItem(auth.storageKey);
            return;
        }
    } catch {
        // fall through to the localStorage scan below
    }

    // Fallback (web only): remove any Supabase auth token key on this origin.
    try {
        if (typeof localStorage !== 'undefined') {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && /^sb-.*-auth-token$/.test(k)) keys.push(k);
            }
            keys.forEach(k => localStorage.removeItem(k));
        }
    } catch {
        // best effort — the subsequent hard redirect re-inits the client from
        // storage anyway, so a leftover key only means the login page redirects.
    }
}
